import { geojsonToArcGIS } from "@terraformer/arcgis";

const DEFAULT_LIMITS = {
  maxTargetFeatures: 1000,
  maxTargetCoordinates: 37000,
  maxArcgisSourceIds: 10000,
};

let capabilitiesPromise = null;

const geoprocessBase = () => String(process.env.VUE_APP_API_GEOPROCESS || "").replace(/\/+$/, "");

const responseJson = async response => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || data.message || `${response.status} ${response.statusText}`);
  return data;
};

export const crossLayerCapabilities = async () => {
  if (!capabilitiesPromise) {
    capabilitiesPromise = fetch(`${geoprocessBase()}/cross-layer/capabilities`)
      .then(responseJson)
      .catch(error => {
        capabilitiesPromise = null;
        throw new Error(`El motor de filtros entre capas no está disponible: ${error.message}`);
      });
  }
  return capabilitiesPromise;
};

export const normalizeTargetGeometries = async (geometries, signal) => {
  const response = await fetch(`${geoprocessBase()}/cross-layer/normalize`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ crs: "EPSG:3857", geometries }),
    signal,
  });
  return responseJson(response);
};

// repairCrossLayerGeometries (PB-24_v4): análogo a normalizeTargetGeometries
// pero SIN unir — /cross-layer/repair repara cada feature por separado
// (make_valid + nudge de antimeridiano, sin simplify: esa tolerancia
// existía solo para que el WKT cupiera en un CQL_FILTER de texto, ninguno
// de los consumidores de este camino usa CQL) y devuelve la lista completa.
// Usado cuando el origen es Capa_Terreno o la operación es "intersects" —
// los únicos casos donde no unir es seguro (ver prepareCrossLayer).
export const repairCrossLayerGeometries = async (geometries, signal) => {
  const response = await fetch(`${geoprocessBase()}/cross-layer/repair`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ crs: "EPSG:3857", geometries }),
    signal,
  });
  return responseJson(response);
};

export const effectiveCrossLayerLimits = capabilities => ({
  ...DEFAULT_LIMITS,
  ...((capabilities && capabilities.limits) || {}),
});

export const crossLayerSignature = (sourceLayerId, filter) => `${sourceLayerId}:${JSON.stringify(filter)}`;

// geoJSONToEsri delega en @terraformer/arcgis. La versión anterior aplanaba
// los anillos de un MultiPolygon sin corregir winding a la convención de
// Esri (exterior horario/hueco antihorario — la opuesta a RFC7946), un bug
// latente que nadie reportó porque ArcGIS Server suele ser tolerante;
// geojsonToArcGIS lo corrige gratis. Fuerza wkid 3857 porque la librería
// por defecto asume 4326 (no reproyecta, solo estructura la geometría) y
// todo este módulo trabaja siempre en EPSG:3857.
export const geoJSONToEsri = geometry => {
  if (!geometry) return null;
  const esri = geojsonToArcGIS(geometry);
  esri.spatialReference = { wkid: 3857 };
  return esri;
};

// packAsEsriGeometry (PB-24_v4) concatena los rings/paths/points de cada
// geometría ya convertida a Esri (geoJSONToEsri) en un solo objeto — así es
// como Esri representa nativo un multi-parte (un array plano de anillos/
// rutas/puntos), sin necesitar fusión topológica previa. Reemplaza a
// geoJSONToEsri(artifact.geometry) para el artifact plural de PB-24_v4
// (artifact.geometries, sin unir).
export const packAsEsriGeometry = (geometries, family) => {
  const key = { Polygon: "rings", LineString: "paths", Point: "points" }[family];
  if (!key || !geometries.length) return null;
  const esriParts = geometries.map(geoJSONToEsri);
  const points = key === "points" ? esriParts.flatMap(part => part.points || [[part.x, part.y]]) : null;
  return {
    ...(points ? { points } : { [key]: esriParts.flatMap(part => part[key] || []) }),
    spatialReference: { wkid: 3857 },
  };
};

// esriGeometryType ahora recibe la familia de geometría del artifact
// (`"Polygon" | "LineString" | "Point"`), no una geometría puntual — el
// resultado de packAsEsriGeometry siempre usa la forma plural de Esri
// (rings/paths/points), aunque el origen tenga una sola parte, así que el
// tipo correspondiente también es siempre el plural (esriGeometryMultipoint,
// nunca esriGeometryPoint).
export const esriGeometryType = family => ({
  Polygon: "esriGeometryPolygon",
  LineString: "esriGeometryPolyline",
  Point: "esriGeometryMultipoint",
}[family] || null);
